import camelcase from "camelcase-keys";
import React, { useState } from "react";

import { clsx } from "clsx";
import Spinner from "react-bootstrap/esm/Spinner";
import {
  Icon,
  RenderWhen,
  ThreatStatusLabel,
  Time
} from "../../../../../components";
import { HistoryTableRowProps } from "../../../@types/rldynamicanalysis";
import { ANALYZING_CONSTS } from "../../../consts";
import { useGetSingleRlcsReport } from "../hooks/useGetSingleRlcsReport";
import { convertGoodwareClassification } from "../RlCloudSandboxUtil";
import HistoryMenu from "./HistoryMenu";
import HistoryTableRowItem from "./HistoryTableRowItem";
import RelatedAnalysisTable from "./RelatedAnalysisTable";
import RLCSConfiguration from "./RLCSConfiguration";

const HistoryTableRow: React.FC<HistoryTableRowProps> = ({
  data,
  hash,
  isStoredReport,
  isNetworkReport = false,
  onReportClick
}) => {
  const {
    classification,
    analysisTime,
    analysisDuration,
    configuration,
    analysisId,
    platform,
    geolocation,
    locale,
    riskScore,
    interactive,
    sampleName,
    timeout,
    optionalParameters,
    status
  } = camelcase(data || {});

  const parsedOptionalParameters =
    optionalParameters?.split(", ")?.reduce((acc, item) => {
      const [key, value] = item.split("=");
      return { ...acc, [key]: value };
    }, {} as Record<string, string>) || {};

  const [classificationLabel, classificationIcon]: string[] =
    convertGoodwareClassification(classification);

  const { data: cloudSandboxData } = useGetSingleRlcsReport(
    hash,
    analysisId,
    isNetworkReport
  );
  const relatedAnalysisData = cloudSandboxData?.related_analysis;
  const [showRelatedAnalysis, setShowRelatedAnalysis] = useState(false);

  const isAnalysisRunning = ANALYZING_CONSTS.map(item =>
    item.toLowerCase()
  ).includes(status?.toLowerCase() || "");

  const items = [
    {
      name: "expand_button",
      component: relatedAnalysisData && (
        <Icon
          name={showRelatedAnalysis ? "chevron-down" : "chevron-right"}
          className="dropdown-toggle__chevron-icon"
          size={16}
          data-testid={
            showRelatedAnalysis
              ? "rlcs_related_analysis_collapse"
              : "rlcs_related_analysis_expand"
          }
        />
      )
    },
    {
      name: "classification",
      component: (
        <RenderWhen
          condition={!isAnalysisRunning}
          fallback={<Spinner animation="border" size="sm" variant="primary" />}
        >
          <ThreatStatusLabel
            showColor={false}
            showOrb
            threatStatus={classificationIcon}
            labelText={classificationLabel}
            riskScore={riskScore}
          />
        </RenderWhen>
      )
    },
    { name: "startTime", component: <Time time={analysisTime} /> },
    {
      name: "interactive",
      component: interactive === "true" ? "Interactive" : "Automated"
    },
    {
      name: "duration",
      component: (
        <RenderWhen
          fallback={<Spinner animation="border" size="sm" variant="primary" />}
          condition={!isAnalysisRunning}
        >
          {`${analysisDuration} seconds`}
        </RenderWhen>
      )
    },
    {
      name: "configuration",
      component: (
        <RLCSConfiguration
          configuration={configuration}
          platform={platform}
          geolocation={geolocation}
          locale={locale}
          sampleName={sampleName}
          internetSimulation={
            parsedOptionalParameters?.internet_simulation?.toLowerCase() ===
            "true"
          }
          timeout={timeout}
        />
      )
    },
    {
      name: "actions",
      component: (
        <RenderWhen condition={!isAnalysisRunning}>
          <HistoryMenu
            analysisId={analysisId}
            hash={hash}
            isStoredReport={isStoredReport}
            isNetworkReport={isNetworkReport}
          />
        </RenderWhen>
      )
    }
  ];

  const handleClick = (name: string) => {
    if (isStoredReport || name === "actions" || isAnalysisRunning) return;
    onReportClick(analysisId);
  };

  return (
    <>
      <tr>
        {items.map(({ name, component }) => (
          <HistoryTableRowItem
            key={name}
            className={clsx(isAnalysisRunning && "disabled")}
            onClick={
              name === "expand_button"
                ? () => setShowRelatedAnalysis(!showRelatedAnalysis)
                : () => handleClick(name)
            }
          >
            {component}
          </HistoryTableRowItem>
        ))}
      </tr>
      {!!relatedAnalysisData && showRelatedAnalysis && (
        <tr>
          <td
            colSpan={9}
            className="rl-cloud-sandbox__history-table-related-analysis-row"
          >
            <RelatedAnalysisTable data={relatedAnalysisData} />
          </td>
        </tr>
      )}
    </>
  );
};

export default HistoryTableRow;
